function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = [],
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`,
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open"),
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open"),
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*",
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this),
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min),
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function setLocalStorageItem(key, data) {
  try {
    return localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    // Something went wrong
    return null;
  }
}

function getLocalStorageItem(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    // Something went wrong
    return null;
  }
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options,
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid,
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this),
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

Shopify.formatMoney = function (cents, format) {
  let defaultFormat = window["defaultFormat"] || "0";
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }

  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var separatorSample = window["defaultFormatWithSeparator"] || "10,000.00";

  var formatString = format;

  // If there's no format provided
  if (!formatString) {
    // parse the number in the separatorSample (not using placeholderRegex), and find the separator
    // for example (€10.000,00 => '.' as separator) or '$10,000.00' => ',' as separator) or ('10 000 kr' => ' ' as separator)
    let match = separatorSample.match(
      /(\D*)\d{1,3}(\D*)\d{1,3}(\D*)\d{1,3}(\D*)/,
    );
    let separator = match[2];

    switch (separator) {
      case " ":
        formatString = defaultFormat.replace(
          0,
          "{{ amount_no_decimals_with_space_separator }}",
        );
        break;
      case ",":
        formatString = defaultFormat.replace(
          0,
          "{{ amount_no_decimals_with_comma_separator }}",
        );
        break;
      default:
        formatString = defaultFormat.replace(0, "{{ amount_no_decimals }}");
        break;
    }
  }

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this)),
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)",
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this)),
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary"),
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button"),
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus,
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`,
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus,
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`,
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary"),
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom",
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset,
      )}px`,
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset,
        )}px`,
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`,
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    if (typeof this.onShow === "function") {
      this.onShow();
    }
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    if (typeof this.onHide === "function") {
      this.onHide();
    }
  }
}
customElements.define("modal-dialog", ModalDialog);

class NotifyMeModal extends ModalDialog {
  constructor() {
    super();
    this.form = this.querySelector(".notify-modal-form");
    this.formButton = this.querySelector(
      '.notify-modal-form button[type="submit"]',
    );
    this.spinner = this.formButton.querySelector(".loading__spinner");
    this.notifyMeContent = this.querySelector("#notifyMeContent");
    this.successContent = this.querySelector("#notifyMeSuccess");
    this.form.addEventListener("submit", this.onFormSubmit.bind(this));
  }
  onShow() {
    let eyePower = getLocalStorageItem("p");
    if (eyePower) {
      let [firstPower] = eyePower.split(",");
      if (firstPower) {
        let variantSelect = this.querySelector("select");
        if (variantSelect) {
          let eyePowerOption = [].find.call(
            variantSelect.options,
            (o) => o.label === firstPower,
          );
          if (eyePowerOption) {
            variantSelect.value = eyePowerOption.value;
          }
        }
      }
    }
  }

  onHide() {
    this.successContent.style.display = "none";
    this.notifyMeContent.style.display = "block";
  }

  onFormSubmit(e) {
    e.preventDefault();
    this.formButton.classList.add("loading");
    this.spinner.classList.remove("hidden");
    let email = this.form.elements["notifyMeEmail"]["value"];
    let klaviyoId = this.form.elements["klaviyoId"]["value"];
    let variantId = this.form.elements["variant"]["value"];
    let isPushAllowed = this.form.elements["notifyMePush"]["checked"];
    if (email) {
      fetch(
        "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=" +
          klaviyoId,
        {
          method: "POST",
          headers: {
            revision: "2024-02-15",
            accept: "application/json",
            "content-type": "application/json",
            "x-klaviyo-onsite": "1",
          },
          body: JSON.stringify({
            data: {
              type: "back-in-stock-subscription",
              attributes: {
                channels: ["EMAIL"],
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email: email,
                    },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: "$shopify:::$default:::" + variantId,
                  },
                },
              },
            },
          }),
        },
      )
        .then((data) => {
          this.formButton.classList.remove("loading");
          this.spinner.classList.add("hidden");
          this.successContent.style.display = "block";
          this.notifyMeContent.style.display = "none";
          let pushowl = window["pushowl"];
          if (
            isPushAllowed &&
            pushowl &&
            typeof pushowl["registerForEvent"] === "function"
          ) {
            pushowl["registerForEvent"]("modal-dialog ", false);
          }
          console.log("Successfully subscribed to back in stock");
          // Handle the response data here
        })
        .catch((error) => {
          this.formButton.classList.remove("loading");
          this.spinner.classList.add("hidden");
          // Handle any errors here
          console.error("Failed to subscribe to back in stock", error);
        });
    }
  }
}
customElements.define("notify-me-modal", NotifyMeModal);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container",
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`,
      ),
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
    if (this.dataset.autoPlayVideo === "true") {
      this.playVideo();
    }
  }

  playVideo(focus) {
    const content = document.createElement("div");
    content.appendChild(
      this.querySelector("template").content.firstElementChild.cloneNode(true),
    );

    this.setAttribute("loaded", true);
    const deferredElement = this.appendChild(
      content.querySelector("video, model-viewer, iframe"),
    );
    if (focus) deferredElement.focus();
    if (
      deferredElement.nodeName == "VIDEO" &&
      deferredElement.getAttribute("autoplay")
    ) {
      // force autoplay for safari
      deferredElement.play();
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      this.playVideo(focus);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(
      debounce((entries) => this.initPages(), 100),
    );
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset,
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true },
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    let page = 1;
    if (this.currentPage) {
      page = this.currentPage - 1;
    }
    this.sliderControlButtons[page].classList.add(
      "slider-counter__link--active",
    );
    this.sliderControlButtons[page].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed,
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue,
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset",
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked"),
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);
class MultiPackVariantSelect extends HTMLElement {
  constructor() {
    super();
    this.selectedPack = "multipack-1";
    this.addToCart = document.querySelector(
      `#ProductSubmitButton-${this.dataset.section}`,
    );
    this.updateSelectedForm();
    this.addEventListener("change", (event) => this.handleChange(event));
  }

  handleChange(event) {
    const target = event.target;
    if (target.name === "pack-size") {
      this.selectedPack = target.value;
      this.updateSelectedForm();
    }
  }

  updateSelectedForm() {
    if (this.selectedPack && this.addToCart) {
      this.addToCart.setAttribute("form", this.selectedPack + "-form");
    }
    this.logSelectedPack();
  }

  logSelectedPack() {
    // console.log(`Selected Pack: ${this.selectedPack}`);
  }
}
customElements.define("multipack-variant-select", MultiPackVariantSelect);

class LensesVariantSelects extends MultiPackVariantSelect {
  constructor() {
    super();
    this.variantsMap = this.getUpdatedVariantsMap();
    this.supportedEyePowers = [
      "0.00",
      "-0.50",
      "-0.75",
      "-1.00",
      "-1.25",
      "-1.50",
      "-1.75",
      "-2.00",
      "-2.25",
      "-2.50",
      "-2.75",
      "-3.00",
      "-3.25",
      "-3.50",
      "-3.75",
      "-4.00",
      "-4.25",
      "-4.50",
      "-4.75",
      "-5.00",
      "-5.25",
      "-5.50",
      "-5.75",
      "-6.00",
    ];
    let eyePower = this.getValidEyePower(
      getLocalStorageItem("p"),
      this.dataset.selectedEyePower || "0.00",
    );
    this.saveEyePower(eyePower);
    this.addEventListener("change", (event) => this.handleSelectChange(event));
  }

  getUpdatedVariantsMap() {
    let node = this.querySelector(
      'script[type="application/json"][name="variants"]',
    );
    if (node && node.textContent) {
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        console.error("Failed to parse variant id map", e);
      }
    }
    return {};
  }

  getValidEyePower(selectedEyePower, defaultValue) {
    if (selectedEyePower && selectedEyePower.length) {
      let eyePowers = selectedEyePower.split(",");
      let leftEye, rightEye;
      if (eyePowers[0] && this.supportedEyePowers.indexOf(eyePowers[0]) >= 0) {
        leftEye = eyePowers[0];
      }
      if (eyePowers[1] && this.supportedEyePowers.indexOf(eyePowers[1]) >= 0) {
        rightEye = eyePowers[1];
      }
      let validPower = [leftEye, rightEye].filter((o) => !!o).join(",");
      if (validPower && validPower.length) return validPower;
    }
    return defaultValue || "0.00";
  }

  connectedCallback() {
    this.render();
  }

  handleSelectChange(event) {
    const target = event.target;
    if (target.name === "different-eye-powers") {
      this.toggleDifferentEyePowers(target.checked);
    } else if (target.name === "eye-power-option") {
      this.saveEyePower(target.value);
    } else if (
      target.name === "left-eye-power" ||
      target.name === "right-eye-power"
    ) {
      this.updateDualEyePower();
    } else if (target.name === "pack-size") {
      this.selectedPack = target.value;
      this.updatePackOptions();
      this.updateFormContent();
    }
  }

  toggleDifferentEyePowers(isChecked) {
    const singleEyePower = this.querySelector(".single-eye-power");
    const differentEyePowerDetails = this.querySelector(
      ".different-eye-power-details",
    );
    if (singleEyePower && differentEyePowerDetails) {
      if (isChecked) {
        singleEyePower.style.display = "none";
        differentEyePowerDetails.style.display = "grid";
        // if not multi power in stored value then update to two eye powers
        if (!this.eyePower.includes(",")) {
          this.querySelector('select[name="left-eye-power"]').value =
            this.eyePower;
          this.querySelector('select[name="right-eye-power"]').value =
            this.eyePower;
          this.saveEyePower(`${this.eyePower},${this.eyePower}`);
        }
      } else {
        singleEyePower.style.display = "grid";
        differentEyePowerDetails.style.display = "none";
        // if  multi power in stored value then update to single eye powers
        if (this.eyePower.includes(",")) {
          let [singlePower] = this.eyePower.split(",");
          this.querySelector('select[name="eye-power-option"]').value =
            singlePower;
          this.saveEyePower(singlePower);
        }
      }
      this.updatePackOptions();
    }
  }

  updateDualEyePower() {
    const differentEyePowersChecked = this.querySelector(
      'input[name="different-eye-powers"]',
    ).checked;
    if (differentEyePowersChecked) {
      const leftEyePower = this.querySelector(
        'select[name="left-eye-power"]',
      ).value;
      const rightEyePower = this.querySelector(
        'select[name="right-eye-power"]',
      ).value;
      this.saveEyePower(`${leftEyePower},${rightEyePower}`);
    }
  }

  updatePackOptions() {
    const onePackOption = this.querySelector(
      'input[name="pack-size"][value="multipack-1"]',
    );
    const differentEyePowersChecked = this.querySelector(
      'input[name="different-eye-powers"]',
    ).checked;
    if (onePackOption) {
      if (differentEyePowersChecked) {
        onePackOption.disabled = true;
        const twoPackOption = this.querySelector(
          'input[name="pack-size"][value="multipack-2"]',
        );
        if (this.selectedPack === "multipack-1") {
          this.selectedPack = "multipack-2";
          if (twoPackOption) {
            twoPackOption.checked = true;
          }
        }
      } else {
        onePackOption.disabled = false;
      }
    }
    this.updateSelectedForm();
  }

  updateFormContent() {
    let [leftEyePower, rightEyePower] = this.eyePower.split(",");
    if (!leftEyePower) {
      leftEyePower = "0.00";
    }
    if (!rightEyePower) {
      rightEyePower = leftEyePower;
    }

    // First let's update the single pack form content
    let multipackOneForm = this.querySelector("form#multipack-1-form");
    if (multipackOneForm) {
      let variantInput = multipackOneForm.querySelector('input[name="id"]');
      if (
        variantInput &&
        this.variantsMap[leftEyePower] &&
        this.variantsMap[leftEyePower].variantId
      ) {
        variantInput.value = this.variantsMap[leftEyePower].variantId;
      }
    }

    // Now let's update the two pack form content
    let multipackTwoForm = this.querySelector("form#multipack-2-form");
    if (multipackTwoForm) {
      let leftEyeInput = multipackTwoForm.querySelector(
        'input[name="properties[_left-eye]"]',
      );
      let rightEyeInput = multipackTwoForm.querySelector(
        'input[name="properties[_right-eye]"]',
      );
      let bundleSelectionInput = multipackTwoForm.querySelector(
        'input[name="properties[_bundle_selection]"]',
      );
      if (leftEyeInput && rightEyeInput && bundleSelectionInput) {
        leftEyeInput.value = leftEyePower;
        rightEyeInput.value = rightEyePower;
        bundleSelectionInput.value = `${leftEyePower} <> ${rightEyePower}`;
      }
    }

    // Now let's update the fourth pack form content
    let multipackThirdForm = this.querySelector("form#multipack-3-form");
    if (multipackThirdForm) {
      let leftEyeInput = multipackThirdForm.querySelector(
        'input[name="properties[_left-eye]"]',
      );
      let rightEyeInput = multipackThirdForm.querySelector(
        'input[name="properties[_right-eye]"]',
      );
      let bundleSelectionInput = multipackThirdForm.querySelector(
        'input[name="properties[_bundle_selection]"]',
      );
      if (leftEyeInput && rightEyeInput && bundleSelectionInput) {
        leftEyeInput.value = leftEyePower;
        rightEyeInput.value = rightEyePower;
        bundleSelectionInput.value = `${leftEyePower} <> ${rightEyePower}`;
      }
    }

    // Now let's check the inventory status
    if (
      this.variantsMap &&
      this.variantsMap[leftEyePower] &&
      this.variantsMap[rightEyePower] &&
      this.addToCart &&
      this.addToCart.form
    ) {
      let selectedForm = this.addToCart.getAttribute("form");
      let leftEyeInventory = this.variantsMap[leftEyePower]["inventory"];
      let rightEyeInventory = this.variantsMap[rightEyePower]["inventory"];
      let out_of_stock = false;
      if (selectedForm === "multipack-1-form") {
        if (leftEyeInventory < 1) {
          out_of_stock = true;
        }
      } else if (selectedForm === "multipack-2-form") {
        if (leftEyePower !== rightEyePower) {
          if (leftEyeInventory < 1 || rightEyeInventory < 1) {
            out_of_stock = true;
          }
        } else {
          if (leftEyeInventory < 2) {
            out_of_stock = true;
          }
        }
      } else if (selectedForm === "multipack-3-form") {
        if (leftEyePower !== rightEyePower) {
          if (leftEyeInventory < 2 || rightEyeInventory < 2) {
            out_of_stock = true;
          }
        } else {
          if (leftEyeInventory < 4) {
            out_of_stock = true;
          }
        }
      }

      let submitButtonText = this.addToCart.querySelector("span");
      let notifyButton = document.querySelector(
        "#NotifyMeButton-" + this.dataset.section,
      );
      if (out_of_stock) {
        this.addToCart.setAttribute("disabled", "disabled");
        this.addToCart.classList.remove("button--primary");
        this.addToCart.classList.remove("color-scheme-success");
        this.addToCart.classList.remove("gradient");
        this.addToCart.classList.add("button--secondary");
        if (submitButtonText) {
          submitButtonText.textContent = window.variantStrings.soldOut;
        }
        if (notifyButton) {
          notifyButton.style.display = "block";
        }
      } else {
        this.addToCart.removeAttribute("disabled");
        this.addToCart.classList.remove("button--secondary");
        this.addToCart.classList.add("button--primary");
        this.addToCart.classList.add("color-scheme-success");
        this.addToCart.classList.add("gradient");
        if (submitButtonText) {
          submitButtonText.textContent = window.variantStrings.addToCart;
        }
        if (notifyButton) {
          notifyButton.style.display = "none";
        }
      }
    }
  }

  saveEyePower(eyePower) {
    this.eyePower = this.getValidEyePower(eyePower);
    setLocalStorageItem("p", this.eyePower);
    this.updateFormContent();
    // console.log(`Selected Eye Power: ${this.eyePower}`);
  }

  render() {
    this.setEyePower();
  }

  setEyePower() {
    const isMultiPower = this.eyePower.includes(",");

    if (!isMultiPower) {
      this.querySelector('select[name="eye-power-option"]').value =
        this.eyePower;
    } else {
      const [leftEyePower, rightEyePower] = this.eyePower.split(",");
      this.querySelector('input[name="different-eye-powers"]').checked = true;
      this.toggleDifferentEyePowers(true);
      this.querySelector('select[name="left-eye-power"]').value = leftEyePower;
      this.querySelector('select[name="right-eye-power"]').value =
        rightEyePower;
    }
    this.updatePackOptions();
  }
}

customElements.define("lenses-variant-selects", LensesVariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`,
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this),
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement),
    );
    const quickBulkElement =
      this.closest("quick-order-list") || this.closest("quick-add-bulk");
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min,
        ),
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max,
        ),
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step,
        ),
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class GlobalEShippingBanner extends HTMLElement {
  constructor() {
    super();
    this.parentContainer =
      document.querySelector(this.dataset.parentContainer) ||
      this.parentElement;
    if (this.parentContainer) this.parentContainer.style.display = "none";

    // Check if Global-E is already initialized.
    if (
      document.querySelector("body").getAttribute("ge-active") &&
      document.querySelector("body").getAttribute("ge-active") === "true"
    )
      return this.initCallback();

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.addedNodes.length &&
          [].some.call(
            mutation.addedNodes,
            (node) =>
              node.classList &&
              node.classList.contains("ge-free-shipping-container"),
          )
        ) {
          this.observer.disconnect();
          this.initCallback();
        }
      });
    });
  }

  initCallback() {
    typeof this.globalEInit === "function" && this.globalEInit();
  }

  connectedCallback() {
    this.observer &&
      this.observer.observe(document.body, {
        childList: true,
      });
  }

  parseGlobalEShippingCost(text) {
    try {
      if (text.startsWith("@")) {
        return null;
      } else if (text.endsWith("on all orders")) {
        return 0;
      }
      const value = text.split(" ").reverse()[0];

      // Remove non-digit characters except ',' and '.'
      const step1 = value.replaceAll(/[^0-9,.]/g, "");

      // Remove '.' occurring at the beginning of the string
      const parsedValue = step1.startsWith(".") ? step1.substring(1) : step1;

      // Parse the value as a fraction
      const parsedFloat = parseFloat(parsedValue.replaceAll(",", ""));

      // Assign the parsed value to the corresponding key in the new object
      if (!isNaN(parsedFloat)) {
        return parsedFloat;
      }
    } catch {
      console.error("Failed to parse Global-E Shipping Bar value: " + text);
    }
    return null;
  }

  globalEInit() {
    const freeShippingBanner = document.querySelector("#FreeShippingBanner");

    // Set the innerHTML of the header-promo element to the textContent
    if (freeShippingBanner) {
      let parseNo = this.parseGlobalEShippingCost(
        freeShippingBanner.textContent,
      );
      if (parseNo !== null) {
        this.render(parseNo);
      }
    }
  }

  render(shippingValue) {
    if (!(this.dataset.shippingText || this.dataset.freeShippingText)) return;
    let text =
      shippingValue === 0
        ? this.dataset["freeShippingText"]
        : this.dataset["shippingText"];
    this.innerHTML = text
      .replace("[country]", this.dataset.country)
      .replace(
        "[value]",
        `<strong class="no-break">${Shopify.formatMoney(
          shippingValue * 100,
        )}</strong>`,
      );
    this.parentContainer.style.display = "block";
  }
}

if (!customElements.get("global-e-shipping")) {
  customElements.define("global-e-shipping", GlobalEShippingBanner);
}

class ShippingBar extends GlobalEShippingBanner {
  constructor() {
    super();
    if (
      window &&
      window.Shopify &&
      window.Shopify.shop === "swati-india.myshopify.com"
    ) {
      this.render(0); // We offer free shipping in India
    }
  }
  render(shippingValue) {
    let textNode = this.querySelector(".unlock-shipping-title");
    if (
      !(
        textNode &&
        textNode.dataset.template &&
        textNode.dataset.unlocked &&
        textNode.dataset.cartTotal
      )
    )
      return;
    let remaining = shippingValue * 100 - parseInt(textNode.dataset.cartTotal);
    let remainingPercent = 100 - Math.round(remaining / shippingValue);
    if (remaining <= 0 || shippingValue === 0) {
      textNode.innerHTML = textNode.dataset["unlocked"];
      textNode.parentElement.classList.add("success");
      remainingPercent = 100;
    } else {
      textNode.innerHTML = textNode.dataset["template"].replace(
        "[remaining_amount]",
        `${Shopify.formatMoney(remaining)}`,
      );
    }
    let bar = this.querySelector(".progress-bar");
    if (bar) {
      bar.style.width = `${remainingPercent}%`;
    }
    textNode.parentElement.classList.remove("loading");
    textNode.parentElement.style.display = "block";
  }
}

if (!customElements.get("shipping-bar")) {
  customElements.define("shipping-bar", ShippingBar);
}

class CampaignTimer extends HTMLElement {
  constructor() {
    super();

    if (
      !window.promotional_timer ||
      !(window.promotional_timer && window.promotional_timer.date)
    )
      return;

    // Get the campaign end date time from the window object (it's in iso 8601 format)
    this.endDate = new Date(window.promotional_timer.date).getTime();
    this.init();
  }

  getFormat(days, hours, minutes, seconds) {
    return window.promotional_timer.hide_days
      ? `${hours}h ${minutes}m ${seconds}s`
      : `${
          days && !isNaN(days) ? `${days}d` : ""
        } ${hours}h ${minutes}m ${seconds}s`;
  }

  init() {
    // get the distance between now and the end of the campaign
    const getDistance = () => this.endDate - new Date().getTime();

    if (this.endDate && getDistance() > 0) {
      const x = setInterval(() => {
        const distance = getDistance();
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
        );
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        this.innerHTML = this.getFormat(days, hours, minutes, seconds);

        if (getDistance() < 0) {
          clearInterval(x);
          this.innerHTML = "";
          localStorage.removeItem("fbPromoCode");
          localStorage.removeItem("fbPromoEndTime");
        }
      }, 1000);
    }
  }
}

customElements.define("campaign-timer", CampaignTimer);
